export type BrandfetchLogoType = "icon" | "logo" | "symbol";
export type BrandfetchLogoFormat = "png" | "jpg" | "webp" | "svg";

/**
 * Build a Brandfetch Logo CDN URL for a domain.
 *
 * Uses the free Logo API (https://docs.brandfetch.com/logo-api/overview)
 * which requires only a client ID (no API key / no per-request cost).
 * Returns null when NEXT_PUBLIC_BRANDFETCH_CLIENT_ID is not configured.
 *
 * @param domain  - The website domain (e.g. "example.com")
 * @param type    - Logo variant: "icon" (square), "logo" (horizontal), or "symbol" (default: "icon")
 * @param format  - Optional explicit format (e.g. "png"). When omitted, the CDN
 *                  chooses based on Accept header / defaults (usually webp).
 *                  Force an explicit format when calling from server code where
 *                  content negotiation is unreliable.
 *
 * This module is client-safe — no server-only imports.
 */
export function getBrandfetchLogoUrl(
  domain: string,
  type: BrandfetchLogoType = "icon",
  format?: BrandfetchLogoFormat,
): string | null {
  const clientId = process.env.NEXT_PUBLIC_BRANDFETCH_CLIENT_ID;
  if (!clientId) {
    return null;
  }
  const cleanDomain = domain
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "");
  const pathSuffix = format ? `${type}.${format}` : type;
  return `https://cdn.brandfetch.io/${encodeURIComponent(cleanDomain)}/${pathSuffix}?c=${clientId}`;
}
